import { useEffect, useRef, type JSX } from "react";
import { useWindowSize } from "../../hooks/useWindowSize";
import { SidebarLayoutShowDesktop } from "../../layout/SideBarLayout/SidebarLayout.styles";
import { theme } from "../../theme";
import { V3Button } from "../Button";
import { V3ButtonColor } from "../Button/V3Button.types";
import { V3Loader } from "../Loader";
import { PageTitle } from "../ReusableStyledComponents/PageTitle";
import { V3ModalBase } from "../V3ModalBase";
import { VerticalSpacing } from "../VerticalSpacing";
import {
    V3StickyBottomBarWrapper,
    V3StickyBottomBarContent,
    V3StickyBottomBarWrapperBtnPanel,
    V3StickyTopBarWrapper,
    V3StickyTopBarContent
} from "./StickyBottomBar.styles";
import { createPortal } from "react-dom";

interface BottomBarContentProps {
    maxWidth?: number;
    alignBottom?: boolean;
    isSubmitting: boolean;
    onCancelHandler?: () => void;
    onConfirmHandler: () => void;
    rightSidebuttonConfirmLabel: string;
    leftSideElement?: JSX.Element;
}

const BottomBarContent = ({
    maxWidth = 738,
    alignBottom = false,
    isSubmitting,
    rightSidebuttonConfirmLabel,
    leftSideElement,
    onCancelHandler,
    onConfirmHandler
}: BottomBarContentProps) => (
    <V3StickyBottomBarContent $maxWidth={maxWidth} $alignBottom={alignBottom}>
        <div>{leftSideElement}</div>
        <V3StickyBottomBarWrapperBtnPanel>
            {!isSubmitting ? (
                <>
                    {!!onCancelHandler && (
                        <div>
                            <V3Button text="Cancel" color={V3ButtonColor.secondary} onClick={onCancelHandler} disabled={isSubmitting} />
                        </div>
                    )}
                    <div>
                        <V3Button onClick={onConfirmHandler} text={rightSidebuttonConfirmLabel} minWidth={128} disabled={isSubmitting} />
                    </div>
                </>
            ) : (
                <V3Loader height={42} />
            )}
        </V3StickyBottomBarWrapperBtnPanel>
    </V3StickyBottomBarContent>
);

const Portal = ({ children }: React.PropsWithChildren) => {
    const elRef = useRef<HTMLDivElement | null>(null);

    if (!elRef.current) {
        const div = document.createElement("div");
        div.className = "CustomPortalElement";
        elRef.current = div;
    }

    useEffect(() => {
        const portalRoot = document.body;
        if (elRef.current) portalRoot.appendChild(elRef.current);

        return () => {
            if (elRef.current) portalRoot.removeChild(elRef.current);
        };
    }, []);

    return createPortal(children, elRef.current);
};

interface StickyBottomBarProps extends BottomBarContentProps {
    useMobileModal?: boolean;
    mobileModaltile?: string;
    topBarTitle?: string;
    topBarContent?: JSX.Element;
}

export const StickyBottomBar = ({
    leftSideElement,
    isSubmitting,
    rightSidebuttonConfirmLabel,
    onCancelHandler,
    onConfirmHandler,
    maxWidth = 738,
    useMobileModal = false,
    mobileModaltile,
    topBarTitle,
    topBarContent,
    alignBottom = false
}: StickyBottomBarProps) => {
    const size = useWindowSize();
    const isMobile = (size.width && size.width < theme.v3.layout.sidebarLayout.mobile) || false;

    useEffect(() => {
        if (window.Intercom) {
            window.Intercom("update", { hide_default_launcher: true });
        }

        return () => {
            if (window.Intercom) {
                window.Intercom("update", { hide_default_launcher: false });
            }
        };
    }, []);

    return (
        <Portal>
            <SidebarLayoutShowDesktop>
                {(!!topBarTitle || !!topBarContent) && (
                    <V3StickyTopBarWrapper>
                        <V3StickyTopBarContent>
                            <div>
                                <PageTitle>{topBarTitle}</PageTitle>
                            </div>
                            <div>
                                <VerticalSpacing top={4}>{topBarContent}</VerticalSpacing>
                            </div>
                        </V3StickyTopBarContent>
                    </V3StickyTopBarWrapper>
                )}
            </SidebarLayoutShowDesktop>
            {isMobile && useMobileModal ? (
                <V3ModalBase isOpen onClose={() => onCancelHandler && onCancelHandler()} title={mobileModaltile}>
                    <BottomBarContent
                        maxWidth={maxWidth}
                        alignBottom={alignBottom}
                        isSubmitting={isSubmitting}
                        rightSidebuttonConfirmLabel={rightSidebuttonConfirmLabel}
                        leftSideElement={leftSideElement}
                        onCancelHandler={onCancelHandler}
                        onConfirmHandler={onConfirmHandler}
                    />
                </V3ModalBase>
            ) : (
                <V3StickyBottomBarWrapper>
                    <BottomBarContent
                        maxWidth={maxWidth}
                        alignBottom={alignBottom}
                        isSubmitting={isSubmitting}
                        rightSidebuttonConfirmLabel={rightSidebuttonConfirmLabel}
                        leftSideElement={leftSideElement}
                        onCancelHandler={onCancelHandler}
                        onConfirmHandler={onConfirmHandler}
                    />
                </V3StickyBottomBarWrapper>
            )}
        </Portal>
    );
};
